import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowDown, ArrowRight, ArrowUp, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { StatusPill } from "../../components/StatusPill";

export default function ClinicianDashboard() {
  const [cases, setCases] = useState(null);
  const [sortKey, setSortKey] = useState("updated");
  const [sortDir, setSortDir] = useState("desc");
  const navigate = useNavigate();

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortedCases = useMemo(() => {
    if (!cases) return null;
    const val = (c) => {
      switch (sortKey) {
        case "client": return (c.client?.name || "").toLowerCase();
        case "evaluation": return (c.product?.short || "").toLowerCase();
        case "interview": return c.appointment?.start || "";
        case "status": return c.status || "";
        case "flags": return (c.flags || []).length;
        default: return c.updated_at || "";
      }
    };
    return [...cases].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [cases, sortKey, sortDir]);

  const SortHeader = ({ label, k, className = "" }) => (
    <th className={`px-6 py-4 ${className}`}>
      <button type="button" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}
        className="inline-flex items-center gap-1.5 uppercase tracking-[0.15em] hover:text-[#1E3F20] transition-colors">
        {label}
        {sortKey === k
          ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  useEffect(() => {
    api.get("/clinician/cases").then((r) => setCases(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  return (
    <DashboardShell title="Assessment dashboard" subtitle="Your assigned evaluations, with scores and flags ready before each interview.">
      {cases === null ? (
        <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
      ) : sortedCases.length === 0 ? (
        <div className="card-warm p-12 text-center max-w-xl" data-testid="no-cases">
          <p className="font-heading text-2xl font-semibold">No assigned cases yet</p>
          <p className="text-sm text-[#5C5C57] mt-2">Cases appear here when a client books one of your availability slots. Add availability to get started.</p>
        </div>
      ) : (
        <div className="card-warm overflow-hidden" data-testid="clinician-cases-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                <SortHeader label="Client" k="client" />
                <SortHeader label="Evaluation" k="evaluation" className="hidden md:table-cell" />
                <SortHeader label="Interview" k="interview" className="hidden lg:table-cell" />
                <SortHeader label="Status" k="status" />
                <SortHeader label="Flags" k="flags" className="hidden lg:table-cell" />
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {sortedCases.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E5DF] last:border-0 hover:bg-[#FAFAF8] transition-colors cursor-pointer"
                  onClick={() => navigate(`/clinician/cases/${c.id}`)} data-testid={`clinician-case-row-${c.id}`}>
                  <td className="px-6 py-4">
                    <Link to={`/clinician/clients/${c.client?.id}`} onClick={(e) => e.stopPropagation()}
                      data-testid={`client-link-${c.id}`} className="font-semibold text-[#1E3F20] hover:underline">
                      {c.client?.name}
                    </Link>
                    <p className="text-xs text-[#5C5C57]">{c.client?.email}</p>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell text-[#5C5C57]">{c.product?.short}</td>
                  <td className="px-6 py-4 hidden lg:table-cell text-[#5C5C57]">
                    {c.appointment ? new Date(c.appointment.start).toLocaleString() : "—"}
                  </td>
                  <td className="px-6 py-4"><StatusPill status={c.status} /></td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {c.flags?.length ? (
                      <span className="inline-flex items-center gap-1.5 text-[#8C2A2A] text-xs font-semibold" data-testid={`case-flags-${c.id}`}>
                        <AlertTriangle className="h-3.5 w-3.5" /> {c.flags.length} flag{c.flags.length > 1 ? "s" : ""}
                      </span>
                    ) : <span className="text-xs text-[#5C5C57]">None</span>}
                  </td>
                  <td className="px-6 py-4 text-right"><ArrowRight className="h-4 w-4 text-[#7E9B82] inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
